import { useEffect, useMemo, useState } from "react";

import { busStudentList, busList } from "../../api/Transport";
import { imgBaseUrl } from "../../api/api";

import { SweetAlertShow } from "../master/Master";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { formatToDate } from "../master/FormatToDateTime";
import Master from "./Master";
import Pagination from "../components/Pagination";
import { getBatchList } from "../../api/BatchService";
import usePrivateLink from "../config/usePrivateLink";

/* ------------------------------------------------------------------ */
/* Pass markup + styles (one source of truth: preview AND print)       */
/* All classes are prefixed bp- so they never clash with bootstrap.    */
/* ------------------------------------------------------------------ */
const PASS_CSS = `
/* ---------- Design 1 : landscape (existing) ---------- */
.bp-sheet{display:grid;grid-template-columns:repeat(auto-fill,92mm);gap:5mm;font-family:Arial,Helvetica,sans-serif;color:#111}
.bp-pass{width:92mm;height:62mm;border:.4mm solid #111;border-radius:3mm;overflow:hidden;background:#fff;
  page-break-inside:avoid;break-inside:avoid;display:flex;flex-direction:column;box-sizing:border-box}
.bp-pass *{box-sizing:border-box}
.bp-top{background:#f5b800;padding:1.4mm 3mm;display:flex;align-items:center;gap:2.5mm}
.bp-top img{height:9mm;width:9mm;object-fit:contain;flex:none}
.bp-school{flex:1;text-align:center;min-width:0}
.bp-school b{display:block;font-size:10.5pt;line-height:1.1}
.bp-school span{display:block;font-size:5.8pt;line-height:1.25}
.bp-strip{background:#111;color:#f5b800;display:flex;justify-content:space-between;padding:.8mm 3mm;font-size:7pt;letter-spacing:.3mm}
.bp-strip b{color:#fff;font-weight:600}
.bp-body{display:flex;gap:2.5mm;padding:2mm 3mm;flex:1;min-height:0}
.bp-photo{width:18mm;height:22mm;border:.3mm solid #999;flex:none;overflow:hidden;background:#eee}
.bp-photo img{width:100%;height:100%;object-fit:cover}
.bp-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:20pt;color:#999}
.bp-info{flex:1;min-width:0}
.bp-name{font-size:9.5pt;font-weight:700;margin-bottom:1mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bp-row{font-size:7pt;line-height:1.5;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bp-row i,.bp-route i{font-style:normal;color:#666;display:inline-block;min-width:12mm}
.bp-route{display:flex;gap:4mm;padding:1mm 3mm;border-top:.3mm dashed #999;font-size:7.5pt}
.bp-route div{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bp-route i{min-width:0;margin-right:1.5mm}
.bp-foot{display:flex;justify-content:space-between;align-items:center;gap:2mm;padding:1mm 3mm;background:#f2f2f2;font-size:6pt}
.bp-foot img{height:6mm;max-width:20mm;object-fit:contain}

/* ---------- Design 2 : portrait ID-card ---------- */
.bp-sheet.bp-d2{grid-template-columns:repeat(auto-fill,58mm);gap:4mm}
.bp2-pass{width:58mm;height:92mm;border:.4mm solid #0d3b66;border-radius:3mm;overflow:hidden;background:#fff;
  page-break-inside:avoid;break-inside:avoid;display:flex;flex-direction:column;box-sizing:border-box}
.bp2-pass *{box-sizing:border-box}
.bp2-head{background:#0d3b66;color:#fff;text-align:center;padding:2mm 2mm 9mm}
.bp2-head img{height:8mm;width:8mm;object-fit:contain;display:block;margin:0 auto 1mm;background:#fff;border-radius:50%}
.bp2-head b{display:block;font-size:8.5pt;line-height:1.15}
.bp2-head span{display:block;font-size:5.2pt;line-height:1.3;opacity:.9}
.bp2-photo{width:20mm;height:20mm;border-radius:50%;border:.8mm solid #fff;margin:-8mm auto 0;overflow:hidden;
  background:#e5e5e5;position:relative;box-shadow:0 0 0 .3mm #0d3b66;flex:none}
.bp2-photo img{width:100%;height:100%;object-fit:cover}
.bp2-ph{width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:18pt;color:#888}
.bp2-name{text-align:center;font-size:9pt;font-weight:700;margin-top:1.2mm;padding:0 2mm;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bp2-tag{margin:1mm auto 1.2mm;background:#f5b800;color:#111;font-size:6pt;font-weight:700;letter-spacing:.2mm;
  padding:.6mm 3mm;border-radius:10mm}
.bp2-info{padding:0 3.5mm;flex:1;min-height:0}
.bp2-row{display:flex;font-size:6.6pt;line-height:1.55;border-bottom:.2mm dotted #bbb}
.bp2-row i{font-style:normal;color:#666;width:13mm;flex:none}
.bp2-row span{flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-weight:600}
.bp2-route{margin:1mm 3mm;padding:1mm 2mm;background:#eef3f8;border-radius:1.5mm;font-size:6.8pt;line-height:1.45}
.bp2-route div{white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.bp2-route i{font-style:normal;color:#0d3b66;margin-right:1mm}
.bp2-foot{background:#0d3b66;color:#fff;display:flex;justify-content:space-between;align-items:center;gap:2mm;
  padding:1mm 3mm;font-size:5.3pt;line-height:1.35;min-height:8mm}
.bp2-foot img{height:6mm;max-width:16mm;object-fit:contain;background:#fff;border-radius:.5mm}
`;

const esc = (v) =>
  String(v ?? "").replace(
    /[&<>"']/g,
    (c) =>
      ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[
      c
      ],
  );

/* ---------------- Design 1 (landscape) ---------------- */
const passHtml1 = (p, school) => {
  const photo = p.photo_url
    ? `<img src="${esc(p.photo_url)}" />`
    : `<div class="bp-ph">${esc((p.student_name || "?").charAt(0))}</div>`;
  return `
  <div class="bp-pass">
    <div class="bp-top">
      ${school.logo ? `<img src="${esc(school.logo)}" />` : ""}
      <div class="bp-school"><b>${esc(school.name)}</b><span>${esc(school.address)}</span>${school.contact ? `<span>${esc(school.contact)}</span>` : ""}</div>
    </div>
    <div class="bp-strip"><span>SCHOOL BUS PASS</span><b>${esc(p.pass_no)}</b></div>
    <div class="bp-body">
      <div class="bp-photo">${photo}</div>
      <div class="bp-info">
        <div class="bp-name">${esc(p.student_name)}</div>
        <div class="bp-row"><i>Reg. No</i>${esc(p.registration_no || "-")}</div>
        ${p.class_name ? `<div class="bp-row"><i>Class</i>${esc(p.class_name)}</div>` : ""}
        <div class="bp-row"><i>Batch</i>${esc(p.batch_name || "-")}</div>
        <div class="bp-row"><i>Father</i>${esc(p.father_name || "-")}</div>
        <div class="bp-row"><i>Mobile</i>${esc(p.phone || "-")}</div>
      </div>
    </div>
    <div class="bp-route">
      <div><i>Bus</i><b>${esc(p.bus_number)}</b></div>
      <div><i>Stop</i><b>${esc(p.stop_name || "-")}</b></div>
    </div>
    <div class="bp-foot">
      <span>Driver: ${esc(p.driver_name || "-")} (${esc(p.driver_number || "-")})<br/>Session: ${esc(p.session_label)}${p.valid_till ? ` · Till ${esc(p.valid_till)}` : ""}</span>
      ${school.signature ? `<img src="${esc(school.signature)}" />` : ""}
    </div>
  </div>`;
};

/* ---------------- Design 2 (portrait ID card) ---------------- */
const passHtml2 = (p, school) => {
  const photo = p.photo_url
    ? `<img src="${esc(p.photo_url)}" />`
    : `<div class="bp2-ph">${esc((p.student_name || "?").charAt(0))}</div>`;
  return `
  <div class="bp2-pass">
    <div class="bp2-head">
      ${school.logo ? `<img src="${esc(school.logo)}" />` : ""}
      <b>${esc(school.name)}</b>
      <span>${esc(school.address)}</span>
      ${school.contact ? `<span>${esc(school.contact)}</span>` : ""}
    </div>
    <div class="bp2-photo">${photo}</div>
    <div class="bp2-name">${esc(p.student_name)}</div>
    <div style="text-align:center"><span class="bp2-tag">BUS PASS · ${esc(p.pass_no)}</span></div>
    <div class="bp2-info">
      <div class="bp2-row"><i>Reg. No</i><span>${esc(p.registration_no || "-")}</span></div>
      ${p.class_name ? `<div class="bp2-row"><i>Class</i><span>${esc(p.class_name)}</span></div>` : ""}
      <div class="bp2-row"><i>Batch</i><span>${esc(p.batch_name || "-")}</span></div>
      <div class="bp2-row"><i>Father</i><span>${esc(p.father_name || "-")}</span></div>
      <div class="bp2-row"><i>Mobile</i><span>${esc(p.phone || "-")}</span></div>
    </div>
    <div class="bp2-route">
      <div><i>Bus:</i><b>${esc(p.bus_number)}</b></div>
      <div><i>Stop:</i><b>${esc(p.stop_name || "-")}</b></div>
      <div><i>Driver:</i>${esc(p.driver_name || "-")} (${esc(p.driver_number || "-")})</div>
    </div>
    <div class="bp2-foot">
      <span>Session: ${esc(p.session_label)}${p.valid_till ? `<br/>Valid till ${esc(p.valid_till)}` : ""}</span>
      ${school.signature ? `<img src="${esc(school.signature)}" />` : ""}
    </div>
  </div>`;
};

const passHtml = (p, school, design = 1) =>
  design === 2 ? passHtml2(p, school) : passHtml1(p, school);

const printPasses = (passes, school, design = 1) => {
  const w = window.open("", "_blank", "width=900,height=700");
  if (!w) {
    SweetAlertShow("info", "Popup blocked", "Allow popups to print passes.");
    return;
  }
  const cols = design === 2 ? "58mm 58mm 58mm" : "92mm 92mm";
  w.document.write(`<!doctype html><html><head><title>Bus Passes</title>
  <style>
    @page{size:A4;margin:8mm}
    *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
    body{margin:0}
    ${PASS_CSS}
    .bp-sheet{grid-template-columns:${cols}}
  </style></head><body>
  <div class="bp-sheet ${design === 2 ? "bp-d2" : ""}">${passes.map((p) => passHtml(p, school, design)).join("")}</div>
  <script>window.onload=()=>setTimeout(()=>window.print(),400);</script></body></html>`);
  w.document.close();
};

/* ------------------------------------------------------------------ */
/* Page                                                                */
/* ------------------------------------------------------------------ */
export default function BusPass() {
  const hasAccess = usePrivateLink();
  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState(null); // paginated object from API
  const [buses, setBuses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState({}); // { [bus_student.id]: row } – survives pagination
  const [passes, setPasses] = useState([]); // generated passes (preview)
  const [design, setDesign] = useState(1); // 1 = landscape, 2 = portrait ID card

  const [filters, setFilters] = useState({
    search: "",
    bus_id: "",
    batch_id: "",
    session_id: "",
    sort_by: "name",
    sort_order: "asc",
    page: 1,
  });

  // School details – same source as AdmitCard
  const school = useMemo(() => {
    const s = JSON.parse(localStorage.getItem("setting")) || {};
    return {
      name: s.name || "School Name",
      address: [s.village, s.district, s.state].filter(Boolean).join(", "),
      contact: [s.mobile, s.secondary_mobile].filter(Boolean).join(" | "),
      logo: s.logo_img ? `${imgBaseUrl}${s.logo_img}` : "/files/logo.png",
      signature: s.signature ? `${imgBaseUrl}${s.signature}` : "",
    };
  }, []);

  useEffect(() => {
    setSessions(JSON.parse(localStorage.getItem("sessions") ?? "[]"));
    (async () => {
      try {
        const res = await busList("limit=all");
        if (res?.data?.status) setBuses(res.data.data || []);
      } catch (e) {
        console.error("Bus list error:", e);
      }
    })();
  }, []);

  const getBatches = async (sessionId) => {
    try {
      const query = sessionId
        ? `&session_id=${sessionId}&limit=all`
        : "limit=all";

      const res = await getBatchList(query);

      if (res?.data?.status) {
        setBatches(res?.data?.data || res?.data?.batches || []);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Batch list error:", error);
      setBatches([]);
    }
  };

  useEffect(() => {
    if (!filters.session_id) return;

    getBatches(filters.session_id);
  }, [filters.session_id]);

  useEffect(() => {
    const active = sessions.find((s) => Number(s.status) === 1)?.id;
    setFilters((p) =>
      p.session_id || !active ? p : { ...p, session_id: active },
    );
  }, [sessions]);

  const clearFilters = () => {
    setSelected({});
    setPasses([]);

    setFilters((prev) => ({
      ...prev,
      search: "",
      bus_id: "",
      batch_id: "",
      page: 1,
    }));
  };

  const getStudents = async () => {
    setLoading(true);
    try {
      const q = Object.entries(filters)
        .filter(([, v]) => v !== "" && v != null)
        .map(([k, v]) => `&${k}=${encodeURIComponent(v)}`)
        .join("");
      const res = await busStudentList(q);
      if (res?.data?.status) setStudents(res.data.data);
      else
        SweetAlertShow(
          "info",
          "Warning",
          res?.data?.message || "Unable to load students.",
        );
    } catch (e) {
      SweetAlertShow(
        "error",
        "Error",
        e?.response?.data?.message ?? e?.message ?? "Something went wrong!",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (filters.bus_id) getStudents();
    else setStudents(null);
  }, [filters]);

  const change = (key, value) => {
    if (key === "bus_id" || key === "batch_id") {
      setSelected({});
      setPasses([]);
    }

    setFilters((p) => ({
      ...p,
      [key]: value,
      page: 1,
    }));
  };

  const rows = students?.data ?? [];
  const selectedRows = Object.values(selected);
  const allOnPage = rows.length > 0 && rows.every((r) => selected[r.id]);

  const toggle = (row) =>
    setSelected((p) => {
      const n = { ...p };
      n[row.id] ? delete n[row.id] : (n[row.id] = row);
      return n;
    });

  const togglePage = () =>
    setSelected((p) => {
      const n = { ...p };
      rows.forEach((r) => (allOnPage ? delete n[r.id] : (n[r.id] = r)));
      return n;
    });

  // Build pass objects from the rows we already have – no extra API / table
  const generate = () => {
    const sessionLabel =
      sessions.find((s) => String(s.id) === String(filters.session_id))
        ?.session ?? "";
    const filterBatchTitle =
      batches.find((b) => String(b.id) === String(filters.batch_id))?.title ??
      null;
    const list = selectedRows.map((r) => ({
      pass_no: `BP${filters.session_id}-${String(r.id).padStart(5, "0")}`,
      student_name: r.student?.name,
      registration_no: r.student?.registration_no,
      father_name: r.student?.father_name,
      phone: r.student?.phone,
      class_name: r.student?.class_name ?? null, // see backend note
      batch_name:
        r.student?.batch_name ??
        r.student?.batch?.title ??
        r.batch?.title ??
        filterBatchTitle,
      photo_url: r.student?.profile_picture
        ? `${imgBaseUrl}${r.student.profile_picture}`
        : null,
      bus_number: r.bus?.bus_number,
      driver_name: r.bus?.driver_name,
      driver_number: r.bus?.driver_number,
      stop_name: r.stop?.stop_name,
      session_label: sessionLabel,
      valid_till: r.end_date ? formatToDate(r.end_date) : null,
    }));
    setPasses(list);
    setTimeout(
      () =>
        document
          .getElementById("bp-preview")
          ?.scrollIntoView({ behavior: "smooth" }),
      100,
    );
  };

  const Skeleton = () =>
    Array.from({ length: 6 }).map((_, i) => (
      <tr key={i}>
        {Array.from({ length: 7 }).map((__, j) => (
          <td key={j}>
            <div className="placeholder-glow">
              <span className="placeholder col-8" />
            </div>
          </td>
        ))}
      </tr>
    ));

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <Header />
        <div className="layout-page">
          <Nav />
          <div className="content-wrapper">
            <div className="container-fluid mt-2">
              <div className="row py-5">
                <Master />
                <div className="col-12">
                  {/* Title + actions */}
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
                      <div>
                        <h5 className="mb-1 fw-bold">Bus Pass</h5>
                        <div className="text-muted small">
                          {selectedRows.length} selected
                          {students
                            ? ` · ${students.total} students on this bus`
                            : ""}
                        </div>
                      </div>
                      <div className="d-flex gap-2">
                        <select
                          className="form-select"
                          style={{ width: 170 }}
                          value={design}
                          onChange={(e) => setDesign(Number(e.target.value))}
                        >
                          <option value={1}>Design 1 – Landscape</option>
                          <option value={2}>Design 2 – Portrait ID</option>
                        </select>
                        {selectedRows.length > 0 && (
                          <button
                            className="btn btn-outline-secondary"
                            onClick={() => {
                              setSelected({});
                              setPasses([]);
                            }}
                          >
                            Clear selection
                          </button>
                        )}
                        <button
                          className="btn btn-outline-primary"
                          disabled={!selectedRows.length}
                          onClick={generate}
                        >
                          <i className="ri-bus-2-line me-2" />
                          Generate passes ({selectedRows.length})
                        </button>
                        {hasAccess("/bus/print") && (
                          <button
                            className="btn btn-primary"
                            disabled={!passes.length}
                            onClick={() => printPasses(passes, school, design)}
                          >
                            <i className="ri-printer-line me-2" />
                            Print
                          </button>
                        )}

                      </div>
                    </div>
                  </div>

                  {/* Filters */}
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body p-3">
                      <div className="row g-3 align-items-center">
                        <div className="col-lg-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select rounded-3"
                              value={filters.bus_id}
                              onChange={(e) => change("bus_id", e.target.value)}
                            >
                              <option value="">Select bus</option>
                              {buses.map((b) => (
                                <option key={b.id} value={b.id}>
                                  {b.bus_number}
                                  {b.driver_name ? ` – ${b.driver_name}` : ""}
                                </option>
                              ))}
                            </select>
                            <label>Bus</label>
                          </div>
                        </div>
                        <div className="col-lg-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select rounded-3"
                              value={filters.batch_id}
                              onChange={(e) =>
                                change("batch_id", e.target.value)
                              }
                            >
                              <option value="">All Batches</option>
                              {batches.map((batch) => (
                                <option key={batch.id} value={batch.id}>
                                  {batch.title}
                                </option>
                              ))}
                            </select>
                            <label>Batch</label>
                          </div>
                        </div>
                        <div className="col-lg-4 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="text"
                              className="form-control rounded-3"
                              placeholder="Search name, registration no, phone..."
                              value={filters.search}
                              onChange={(e) => change("search", e.target.value)}
                            />
                            <label>Name / Registration No / Phone</label>
                          </div>
                        </div>
                        <div className="col-lg-2 col-md-6">
                          <button
                            type="button"
                            className="btn btn-outline-secondary rounded-3 w-100"
                            onClick={clearFilters}
                          >
                            <i className="ri-filter-off-line me-2" />
                            Clear Filters
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Student table */}
                  <div className="card border-0 shadow-sm mb-3">
                    <div className="card-body">
                      <div className="table-responsive">
                        <table className="table table-hover mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: 40 }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={allOnPage}
                                  onChange={togglePage}
                                  disabled={!rows.length}
                                />
                              </th>
                              <th>Student</th>
                              <th>Parents</th>
                              <th>Mobile</th>
                              <th>Stop</th>
                              <th>Joining</th>
                            </tr>
                          </thead>
                          <tbody>
                            {!filters.bus_id ? (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-center text-muted py-5"
                                >
                                  <i className="ri-bus-line fs-2 d-block mb-2 text-secondary"></i>
                                  Select a bus to see its students.
                                </td>
                              </tr>
                            ) : loading ? (
                              <Skeleton />
                            ) : rows.length === 0 ? (
                              <tr>
                                <td
                                  colSpan="6"
                                  className="text-center text-muted py-5"
                                >
                                  No students found on this bus.
                                </td>
                              </tr>
                            ) : (
                              rows.map((r) => (
                                <tr
                                  key={r.id}
                                  onClick={() => toggle(r)}
                                  style={{ cursor: "pointer" }}
                                >
                                  <td onClick={(e) => e.stopPropagation()}>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={!!selected[r.id]}
                                      onChange={() => toggle(r)}
                                    />
                                  </td>
                                  <td>
                                    <div className="fw-semibold">
                                      {r.student?.name}
                                    </div>
                                    <small className="text-muted">
                                      {r.student?.registration_no}
                                    </small>
                                  </td>
                                  <td>
                                    <div>{r.student?.father_name || "-"}</div>
                                    <small className="text-muted">
                                      {r.student?.mother_name || "-"}
                                    </small>
                                  </td>
                                  <td>{r.student?.phone || "-"}</td>
                                  <td>{r.stop?.stop_name || "-"}</td>
                                  <td>{formatToDate(r.joining_date)}</td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                        {students && (
                          <Pagination
                            data={students}
                            setFilters={setFilters}
                            filters={filters}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Preview */}
                  {passes.length > 0 && (
                    <div
                      className="card border-0 shadow-sm mb-4"
                      id="bp-preview"
                    >
                      <div className="card-header bg-transparent">
                        <h6 className="mb-0 fw-bold">
                          Preview · {passes.length} pass
                          {passes.length > 1 ? "es" : ""}
                        </h6>
                      </div>
                      <div className="card-body" style={{ overflowX: "auto" }}>
                        <style>{PASS_CSS}</style>
                        <div
                          className={`bp-sheet ${design === 2 ? "bp-d2" : ""}`}
                          dangerouslySetInnerHTML={{
                            __html: passes
                              .map((p) => passHtml(p, school, design))
                              .join(""),
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
